/**
 * FormsTab.tsx
 *
 * Patient-detail "Forms" section. Mirrors the legacy patient-survey-list.php
 * tab (scoped to surveys_responses + patients_form_submissions where
 * formType is Demographics / History / Financing, per product decision).
 *
 * Columns: Form | Signature | Date (sorted by Date desc).
 * Clicking a row opens the appropriate read-only detail modal.
 */
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Circle, Search } from 'lucide-react';
import { Spinner } from '@/components/ui';
import { listPatientForms, type FormListRow } from '@/services/patientForms.service';
import { EFormsModal } from './EFormsModal';
import { GiFormSendModal } from './GiFormSendModal';
import { SurveyResponseModal } from './SurveyResponseModal';
import { FormSubmissionModal } from './FormSubmissionModal';
import { InvestigateFormsModal } from './InvestigateFormsModal';
import { MoveFormModal } from './MoveFormModal';

interface FormsTabProps {
  patientId: number;
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function FormsTab({ patientId }: FormsTabProps) {
  const [eFormsOpen, setEFormsOpen] = useState(false);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [openSurveyResponse, setOpenSurveyResponse] = useState<number | null>(null);
  const [openSubmission, setOpenSubmission] = useState<number | null>(null);
  const [movingResponse, setMovingResponse] = useState<number | null>(null);
  const [showDeleted, setShowDeleted] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['patient-forms-list', patientId, showDeleted],
    queryFn: () => listPatientForms(patientId, showDeleted),
  });

  const rows: FormListRow[] = data?.rows ?? [];
  const giFormConfigured = data?.giFormConfigured ?? false;
  const giFormUpToDate = data?.giFormUpToDate ?? false;
  const currentGiFormId = data?.currentGiFormId ?? null;
  const [giSendOpen, setGiSendOpen] = useState(false);

  const handleRowClick = (row: FormListRow) => {
    if (row.source === 'survey_response') {
      setOpenSurveyResponse(row.id);
    } else {
      setOpenSubmission(row.id);
    }
  };

  return (
    <div className="flex flex-col h-full">
      {/*
        SCRUM-1430. Four controls in an unwrapped `justify-between` row at 375px, and
        `px-6` spends 48px of it. The "Show deleted" label was the only item in the
        group that could give — its two siblings are `inline-flex … px-3` buttons whose
        text is one unbreakable token — so it wrapped to two lines, which also made it
        ~40px tall against their fixed `h-8`, i.e. taller than the buttons it sits
        beside. `shrink-0 whitespace-nowrap` stops the wrap; the row wrapping stops the
        overflow that forced it.
      */}
      <div className="flex-shrink-0 flex items-center justify-between px-6 pt-4 pb-2 max-md:flex-wrap max-md:gap-2 max-md:px-4">
        <h2 className="text-lg font-semibold text-slate-900">Forms</h2>
        <div className="flex items-center gap-3 max-md:w-full max-md:flex-wrap">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer select-none max-md:shrink-0 max-md:whitespace-nowrap">
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
              className="rounded border-slate-300"
            />
            Show deleted
          </label>
          <button
            type="button"
            onClick={() => setInvestigateOpen(true)}
            className="inline-flex h-8 items-center gap-1.5 justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
            title="Find completed forms in the comm log that were never linked to this patient"
          >
            <Search className="h-3.5 w-3.5" />
            Investigate
          </button>
          <button
            type="button"
            onClick={() => setEFormsOpen(true)}
            className="inline-flex h-8 items-center justify-center rounded-md border border-slate-300 bg-white px-3 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50"
          >
            E-Forms
          </button>
        </div>
      </div>

      {giFormConfigured && !giFormUpToDate && !isLoading && (
        /* The tab-local twin of the page-level GI banner, which was given the same
           treatment in the previous batch. Only one of the two renders at a time (the
           page-level one is suppressed on this section), so this is a second instance
           of the same defect rather than a duplicate. */
        <div className="flex-shrink-0 mx-6 mb-2 flex items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 max-md:mx-4 max-md:flex-wrap max-md:mt-1 max-md:mb-3">

          {/* SCRUM-1431: the message wrapped one word per line. `max-md:flex-wrap` on the
              row above was already here and is not enough on its own — with no `min-w-0`
              this group's automatic minimum size is its longest word, and its sibling
              button group is `flex-shrink-0`, so the row never reaches the width at which
              it would wrap and the text is squeezed to a column instead. `min-w-0` supplies
              the zero minimum and `flex-1` claims the free width, which is the pair the
              page-level twin already carries (PatientDetailPage) — it is the reason that
              one reads normally and this one did not.

              `max-md:` rather than the twin's unprefixed tokens: at desktop widths this row
              fits, so the pair is almost certainly inert there — but "almost certainly" is
              an argument and the prefix is provable. Worth reconciling the two spellings
              separately.

              `mt-1`/`mb-3` are the reported crowding: the banner had no top margin against
              a header row that grows taller when it wraps, and 8px to the content below. */}
          <div className="flex items-start gap-2 max-md:min-w-0 max-md:flex-1">
            <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500" />
            <span>This patient has not completed the current General Intake form.</span>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setInvestigateOpen(true)}
              className="inline-flex h-7 items-center rounded-md border border-amber-300 bg-white px-3 text-xs font-semibold text-amber-800 transition-colors hover:bg-amber-100"
            >
              Already filled out?
            </button>
            <button
              type="button"
              onClick={() => setGiSendOpen(true)}
              className="inline-flex h-7 items-center rounded-md bg-amber-600 px-3 text-xs font-semibold text-white transition-colors hover:bg-amber-700"
            >
              Send Now
            </button>
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : isError ? (
        <p className="px-6 py-4 text-sm text-red-600">Failed to load forms.</p>
      ) : rows.length === 0 ? (
        <p className="px-6 py-4 text-sm text-slate-400 italic">No forms found for this patient.</p>
      ) : (
        <div className="min-h-0 flex-1 overflow-auto">
          <table className="min-w-full divide-y divide-slate-100 text-sm">
            <thead className="bg-slate-50 sticky top-0 z-10">
              <tr className="text-left text-xs font-medium uppercase tracking-wide text-slate-500">
                <th className="px-6 py-3">Form</th>
                <th className="px-6 py-3">Signature</th>
                <th className="px-6 py-3 text-right">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr
                  key={`${r.source}-${r.id}`}
                  className={`cursor-pointer transition-colors hover:bg-slate-50 ${r.deleted ? 'opacity-50' : ''}`}
                  onClick={() => handleRowClick(r)}
                >
                  <td className="px-6 py-3 font-medium text-slate-800 underline decoration-transparent hover:decoration-inherit">
                    <span className="inline-flex items-center gap-1.5">
                      {r.source === 'survey_response' && (
                        r.wasRead === true
                          ? <span title="Read"><CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0" /></span>
                          : <span title="Unread"><Circle className="h-4 w-4 text-slate-400 flex-shrink-0" /></span>
                      )}
                      {r.title}
                      {r.isGeneralIntake && r.generalIntakeStatus === 'current' && (
                        <span className="ml-2 inline-flex items-center rounded bg-green-100 px-1.5 py-0.5 text-xs font-semibold text-green-700">General Intake (Current)</span>
                      )}
                      {r.isGeneralIntake && r.generalIntakeStatus === 'old' && (
                        <span className="ml-2 inline-flex items-center rounded bg-slate-100 px-1.5 py-0.5 text-xs font-semibold text-slate-500">General Intake (Old)</span>
                      )}
                      {r.deleted && <span className="ml-2 text-xs font-normal text-red-500">(deleted)</span>}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-slate-700">{r.signatureLabel}</td>
                  <td className="px-6 py-3 text-right text-slate-700">{formatDateTime(r.date)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <EFormsModal
        patientId={patientId}
        open={eFormsOpen}
        onClose={() => setEFormsOpen(false)}
      />

      <InvestigateFormsModal
        patientId={patientId}
        open={investigateOpen}
        onClose={() => setInvestigateOpen(false)}
      />

      {currentGiFormId && (
        <GiFormSendModal
          patientId={patientId}
          surveyId={currentGiFormId}
          open={giSendOpen}
          onClose={() => setGiSendOpen(false)}
        />
      )}

      <SurveyResponseModal
        patientId={patientId}
        responseId={openSurveyResponse}
        open={openSurveyResponse !== null}
        onClose={() => setOpenSurveyResponse(null)}
        onMoveRequested={() => setMovingResponse(openSurveyResponse)}
      />

      <MoveFormModal
        open={movingResponse !== null}
        onClose={() => setMovingResponse(null)}
        fromPatientId={patientId}
        responseId={movingResponse}
        onMoved={() => {
          setMovingResponse(null);
          setOpenSurveyResponse(null);
        }}
      />

      <FormSubmissionModal
        patientId={patientId}
        submissionId={openSubmission}
        open={openSubmission !== null}
        onClose={() => setOpenSubmission(null)}
      />
    </div>
  );
}
